import { MapPin, Phone, Mail, Clock } from "lucide-react";
import SEO, { orgJsonLd, breadcrumbJsonLd } from "../components/SEO";
import Reveal, { Eyebrow } from "../components/Reveal";
import LeadForm from "../components/LeadForm";
import { FIRM, STATES } from "../data/siteData";
import { Link } from "react-router-dom";

export default function ContactPage() {
  return (
    <>
      <SEO
        title="Contact Us — Book a Free Consultation | Complisure Counsultancy"
        description="Talk to a senior CA/CS advisor within 4 business hours. Company registration, Virtual CFO, valuation and fundraising advisory across 31 Indian cities. Call +91 73510 10107."
        canonical="https://compliance-capital-1.preview.emergentagent.com/contact"
        jsonLd={[orgJsonLd, breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }])]}
      />
      <section className="relative overflow-hidden pb-20 pt-36">
        <div className="blob blob-float right-[-8%] top-[-8%] h-96 w-96 bg-brand-blue/10" />
        <div className="dot-grid absolute inset-0 opacity-[0.1]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
          <div className="grid items-start gap-12 lg:grid-cols-2">
            <Reveal>
              <Eyebrow>Contact</Eyebrow>
              <h1 className="mt-3 font-display text-4xl font-extrabold tracking-[-0.02em] text-brand-navy sm:text-5xl">
                Book your <span className="text-gradient">free consultation</span>
              </h1>
              <p className="mt-5 max-w-lg text-base leading-relaxed text-brand-gray sm:text-lg">
                Thirty minutes with a senior CA or CS advisor. You'll leave with a clear roadmap — whether or not we work together.
              </p>

              <div className="mt-8 space-y-4">
                <a href={`tel:${FIRM.phoneRaw}`} data-testid="contact-phone" className="glass card-lift flex items-center gap-4 rounded-2xl p-5">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-brand-blue to-brand-sky text-white"><Phone className="h-5 w-5" /></span>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-brand-gray">Call / WhatsApp</p>
                    <p className="font-display text-lg font-bold text-brand-navy">{FIRM.phoneDisplay}</p>
                  </div>
                </a>
                <a href={`mailto:${FIRM.email}`} data-testid="contact-email" className="glass card-lift flex items-center gap-4 rounded-2xl p-5">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-brand-blue to-brand-sky text-white"><Mail className="h-5 w-5" /></span>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-brand-gray">Email</p>
                    <p className="font-display text-lg font-bold text-brand-navy">{FIRM.email}</p>
                  </div>
                </a>
                <div className="glass flex items-start gap-4 rounded-2xl p-5">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-blue to-brand-sky text-white"><MapPin className="h-5 w-5" /></span>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-brand-gray">Head Office</p>
                    <p className="text-sm font-semibold text-brand-navy">{FIRM.address}</p>
                    <p className="mt-1 flex items-center gap-1.5 text-xs text-brand-gray"><Clock className="h-3 w-3" /> Mon–Sat, 9:30 AM – 7:00 PM IST</p>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-blue">Also serving</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {STATES.map((st) => (
                    <Link key={st.slug} to={`/locations/${st.slug}`} className="rounded-full border border-brand-blue/15 bg-white/70 px-3.5 py-1.5 text-xs font-medium text-brand-navy transition-colors hover:bg-brand-ice hover:text-brand-blue">
                      {st.name}
                    </Link>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.12} className="lg:sticky lg:top-28">
              <LeadForm sourcePage="/contact" />
              <div className="glass mt-6 overflow-hidden rounded-3xl">
                <iframe
                  data-testid="contact-map"
                  title="Complisure Counsultancy head office map — Hyderabad"
                  src={`https://www.google.com/maps?q=${encodeURIComponent("HITEC City, Hyderabad, Telangana, India")}&output=embed`}
                  className="h-64 w-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
